<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import PipelinesAPI from 'dashboard/api/pipelines';
/* global axios */

const props = defineProps({
  accountId: { type: [String, Number], required: true },
});

const emit = defineEmits(['close', 'created']);

const store = useStore();
const PRESET_COLORS = ['#3b82f6', '#06b6d4', '#10b981', '#eab308', '#f97316', '#ef4444', '#ec4899', '#a855f7', '#14b8a6'];

const name = ref('');
const stages = ref([
  { name: 'LEADS ENTRANTES', color: '#3b82f6', stage_type: 'open' },
  { name: 'EN PROCESO', color: '#eab308', stage_type: 'open' },
  { name: 'PERDIDO', color: '#ef4444', stage_type: 'lost' },
  { name: 'LOGRADO', color: '#10b981', stage_type: 'won' },
]);
const selectedBranchIds = ref([]); // [] = global, todas las sucursales
const saving = ref(false);
const errorMsg = ref(null);

const availableBranches = computed(() => store.getters['branches/getBranches'] || []);
function toggleBranch(id) {
  const idx = selectedBranchIds.value.indexOf(id);
  if (idx === -1) selectedBranchIds.value.push(id);
  else selectedBranchIds.value.splice(idx, 1);
}
function selectAllBranches() {
  selectedBranchIds.value = availableBranches.value.map(b => b.id);
}
function clearAllBranches() {
  selectedBranchIds.value = [];
}
const branchSummary = computed(() => {
  const total = availableBranches.value.length;
  const selected = selectedBranchIds.value.length;
  if (selected === 0) return { text: `Sin selección · aplica a TODAS las ${total} sucursales (modo global)`, count: total };
  if (selected === total) return { text: `Las ${total} sucursales seleccionadas`, count: total };
  return { text: `${selected} de ${total} sucursales seleccionadas`, count: selected };
});

onMounted(() => {
  store.dispatch('branches/get');
});

function addStage() {
  const color = PRESET_COLORS[stages.value.length % PRESET_COLORS.length];
  // Insert before terminal stages
  const insertAt = stages.value.findIndex(s => s.stage_type !== 'open');
  const newStage = { name: 'NUEVA ETAPA', color, stage_type: 'open' };
  if (insertAt === -1) stages.value.push(newStage);
  else stages.value.splice(insertAt, 0, newStage);
}

function removeStage(index) {
  stages.value.splice(index, 1);
}

async function save() {
  if (!name.value || stages.value.length === 0) return;
  saving.value = true;
  errorMsg.value = null;
  try {
    const { data: pipeline } = await PipelinesAPI.create({
      pipeline: {
        name: name.value,
        scope_branch_ids: selectedBranchIds.value,
      },
    });
    for (const stage of stages.value) {
      await axios.post(
        `/api/v1/accounts/${props.accountId}/pipelines/${pipeline.id}/stages`,
        { stage }
      );
    }
    emit('created', pipeline);
  } catch (e) {
    errorMsg.value = e.response?.data?.message || e.message;
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <div class="npd-overlay" @click.self="emit('close')">
    <div class="npd-modal" role="dialog" aria-modal="true">
      <!-- HEAD -->
      <div class="npd-head">
        <div class="npd-title-wrap">
          <div class="npd-title-icon">🎯</div>
          <div>
            <div class="npd-title">Nuevo embudo</div>
            <div class="npd-subtitle">Crear pipeline · 1 vez para todas las sucursales</div>
          </div>
        </div>
        <button class="npd-close" type="button" @click="emit('close')" aria-label="Cerrar">×</button>
      </div>

      <!-- BODY scrolleable -->
      <div class="npd-body">
        <div class="npd-field-label">Nombre del embudo</div>
        <input
          v-model="name"
          placeholder="ej: DTO. DECORACIÓN"
          class="npd-input"
        />

        <!-- ETAPAS -->
        <div class="npd-section">
          <div class="npd-section-head">
            <div class="npd-section-title">📋 Etapas / columnas</div>
          </div>
          <div class="npd-stages-list">
            <div
              v-for="(stage, i) in stages"
              :key="i"
              class="npd-stage-card group"
            >
              <div class="npd-stage-row">
                <div class="npd-stage-color-active" :style="{ background: stage.color, boxShadow: `0 0 10px ${stage.color}` }"></div>
                <input v-model="stage.name" type="text" placeholder="Nombre de la etapa" class="npd-stage-name" />
                <select v-model="stage.stage_type" class="npd-stage-type">
                  <option value="open">⚡ Abierto</option>
                  <option value="won">🏆 Logrado</option>
                  <option value="lost">❌ Perdido</option>
                </select>
                <button
                  @click="removeStage(i)"
                  class="npd-stage-x"
                  :disabled="stages.length <= 1"
                  type="button"
                  title="Eliminar etapa"
                >×</button>
              </div>
              <div class="npd-swatches-row">
                <div
                  v-for="c in PRESET_COLORS"
                  :key="c"
                  class="npd-swatch"
                  :class="{ 'is-on': stage.color === c }"
                  :style="{ background: c }"
                  @click="stage.color = c"
                  :title="c"
                ></div>
                <label class="npd-swatch-custom" title="Color personalizado">
                  🎨
                  <input v-model="stage.color" type="color" class="npd-swatch-custom-input" />
                </label>
              </div>
            </div>
          </div>
          <button @click="addStage" type="button" class="npd-add-stage">+ Añadir etapa</button>
        </div>

        <!-- SUCURSALES · P1 multi-checkbox -->
        <div v-if="availableBranches.length > 0" class="npd-section">
          <div class="npd-section-head">
            <div class="npd-section-title">🏢 Sucursales donde aplicar</div>
            <div class="npd-bulk-tools">
              <button type="button" class="npd-bulk-btn npd-bulk-all" @click="selectAllBranches">✓ Todas</button>
              <button type="button" class="npd-bulk-btn npd-bulk-none" @click="clearAllBranches">✗ Ninguna</button>
            </div>
          </div>
          <div class="npd-branches-grid">
            <div
              v-for="b in availableBranches"
              :key="b.id"
              class="npd-br-row"
              :class="{ 'is-on': selectedBranchIds.includes(b.id) }"
              @click="toggleBranch(b.id)"
            >
              <div class="npd-br-cb">
                <span v-if="selectedBranchIds.includes(b.id)">✓</span>
              </div>
              <div class="npd-br-icon">{{ b.code || b.name?.substring(0, 4).toUpperCase() }}</div>
              <div class="npd-br-info">
                <div class="npd-br-name">{{ b.name }}</div>
                <div class="npd-br-meta">{{ b.is_personal ? 'Tu sucursal · Personal' : 'Sucursal' }}</div>
              </div>
              <div class="npd-br-tag" :class="b.is_personal ? 'is-personal' : 'is-suc'">
                {{ b.is_personal ? 'Personal' : 'Suc' }}
              </div>
            </div>
          </div>

          <div class="npd-summary">
            ⚡ {{ branchSummary.text }} · al editar etapas se sincroniza automáticamente.
          </div>
        </div>

        <div v-if="errorMsg" class="npd-error">⚠ {{ errorMsg }}</div>
      </div>

      <!-- FOOT -->
      <div class="npd-foot">
        <button @click="emit('close')" class="npd-btn npd-cancel" type="button">Cancelar</button>
        <button
          @click="save"
          :disabled="saving || !name || stages.length === 0"
          class="npd-btn npd-save"
          type="button"
        >
          {{ saving ? '⏳ Creando…' : `⚡ Crear embudo${branchSummary.count > 1 ? ` en ${branchSummary.count} sucursales` : ''}` }}
        </button>
      </div>
    </div>
  </div>
</template>

<style>
/* ─────────────────────────────────────────────────────────────────
   NewPipelineDialog · M1.B + P1 layout
   Pixel-perfect del mockup pipeline-multibranch-P1-multicheckbox.html
   Prefijo .npd- para evitar colisiones globales.
   ───────────────────────────────────────────────────────────────── */
.npd-overlay {
  position: fixed; inset: 0; z-index: 50;
  background: rgba(0,0,0,0.78);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
  font-family: -apple-system, system-ui, sans-serif;
}
.npd-modal {
  width: 540px; max-width: 100%; max-height: calc(100vh - 48px);
  background: rgba(10,10,10,0.98);
  border: 1.5px solid rgba(251,191,36,0.45);
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 30px 90px rgba(0,0,0,0.85), 0 0 50px rgba(251,191,36,0.18);
  display: flex; flex-direction: column;
  position: relative;
  color: #fff;
}
.npd-modal::before {
  content: ''; position: absolute; inset: 0; pointer-events: none; border-radius: 18px;
  background: linear-gradient(120deg, transparent 12%, rgba(255,255,255,0.04) 22%, transparent 24%),
              radial-gradient(circle at 100% 0%, rgba(251,191,36,0.10), transparent 60%);
  z-index: 1;
}
.npd-modal > * { position: relative; z-index: 2; }

.npd-head {
  padding: 18px 22px 16px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  display: flex; justify-content: space-between; align-items: center;
  background: radial-gradient(ellipse at 0% 0%, rgba(251,191,36,0.10), transparent 60%);
  flex-shrink: 0;
}
.npd-title-wrap { display: flex; align-items: center; gap: 12px; }
.npd-title-icon {
  width: 38px; height: 38px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(251,191,36,0.20), rgba(217,119,6,0.10));
  border: 1.5px solid rgba(251,191,36,0.40);
  display: flex; align-items: center; justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}
.npd-title { font-size: 18px; font-weight: 900; color: #fff; line-height: 1.15; }
.npd-subtitle { font-size: 11px; color: rgba(255,255,255,0.55); margin-top: 1px; }
.npd-close {
  width: 28px; height: 28px;
  border-radius: 50%;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.55);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; font-size: 16px; line-height: 1; padding: 0;
  font-family: inherit;
  transition: all 0.15s;
}
.npd-close:hover {
  background: rgba(239,68,68,0.20); color: #fff;
  border-color: rgba(239,68,68,0.40);
  transform: rotate(90deg);
}

.npd-body {
  padding: 18px 22px;
  flex: 1; overflow-y: auto;
}

.npd-field-label {
  font-size: 11px; letter-spacing: 1.2px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.55); font-weight: 800;
  margin-bottom: 8px;
}
.npd-input {
  width: 100%;
  padding: 10px 14px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(251,191,36,0.30);
  border-radius: 8px;
  color: #fff; font-size: 14px;
  font-family: inherit;
  outline: none;
}
.npd-input:focus {
  border-color: #fbbf24;
  box-shadow: 0 0 0 3px rgba(251,191,36,0.15);
}
.npd-input::placeholder { color: rgba(255,255,255,0.30); }

.npd-section { margin-top: 18px; }
.npd-section-head {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 10px;
}
.npd-section-title {
  font-size: 11px; letter-spacing: 1.2px;
  text-transform: uppercase;
  color: rgba(251,191,36,0.85); font-weight: 800;
}
.npd-bulk-tools { display: flex; gap: 4px; }
.npd-bulk-btn {
  padding: 4px 9px;
  border-radius: 6px;
  font-size: 9px; font-weight: 800;
  cursor: pointer; border: 1px solid;
  font-family: inherit;
}
.npd-bulk-all {
  background: rgba(34,197,94,0.10);
  border-color: rgba(34,197,94,0.45);
  color: #4ade80;
}
.npd-bulk-all:hover { background: rgba(34,197,94,0.20); }
.npd-bulk-none {
  background: rgba(239,68,68,0.10);
  border-color: rgba(239,68,68,0.45);
  color: #f87171;
}
.npd-bulk-none:hover { background: rgba(239,68,68,0.20); }

/* STAGES · S1 layout: row + swatches inline debajo */
.npd-stages-list { display: flex; flex-direction: column; gap: 8px; }
.npd-stage-card {
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 10px;
  padding: 10px 12px;
  transition: border-color 0.15s;
}
.npd-stage-card:hover { border-color: rgba(255,255,255,0.12); }
.npd-stage-row {
  display: flex; align-items: center; gap: 10px;
  margin-bottom: 8px;
}
.npd-stage-color-active {
  width: 30px; height: 30px;
  border-radius: 8px;
  border: 2px solid rgba(255,255,255,0.20);
  flex-shrink: 0;
  transition: all 0.2s;
}
.npd-stage-name {
  flex: 1; min-width: 0;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 6px;
  padding: 7px 10px;
  font-size: 13px; font-weight: 700;
  color: #fff;
  font-family: inherit;
  outline: none;
}
.npd-stage-name:focus { border-color: rgba(251,191,36,0.55); }
.npd-stage-name::placeholder { color: rgba(255,255,255,0.30); font-weight: 500; }
.npd-stage-type {
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 10px;
  color: rgba(255,255,255,0.75);
  font-family: inherit;
  cursor: pointer;
  outline: none;
  font-weight: 700;
}
.npd-stage-type option { background: #0a0a0a; color: #fff; }
.npd-stage-x {
  width: 24px; height: 24px;
  border-radius: 5px;
  background: rgba(239,68,68,0.10);
  border: 1px solid rgba(239,68,68,0.30);
  color: #f87171;
  cursor: pointer;
  font-size: 14px; line-height: 1; padding: 0;
  font-family: inherit;
  transition: all 0.15s;
  flex-shrink: 0;
}
.npd-stage-x:hover {
  background: rgba(239,68,68,0.25);
  color: #fff;
  border-color: rgba(239,68,68,0.55);
}
.npd-stage-x:disabled { opacity: 0.30; cursor: not-allowed; }

/* Swatches inline · S1 */
.npd-swatches-row {
  display: flex;
  gap: 5px;
  padding-left: 40px; /* alinear con el final del color-active */
  align-items: center;
}
.npd-swatch {
  width: 22px; height: 22px;
  border-radius: 5px;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.15s;
}
.npd-swatch:hover { transform: scale(1.08); }
.npd-swatch.is-on {
  border-color: #fff;
  transform: scale(1.10);
  box-shadow: 0 0 10px rgba(251,191,36,0.45);
}
.npd-swatch-custom {
  width: 22px; height: 22px;
  border-radius: 5px;
  cursor: pointer;
  border: 1px dashed rgba(255,255,255,0.35);
  background: linear-gradient(135deg, #ff0080 0%, #00ffd0 50%, #ffd700 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
}
.npd-swatch-custom-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
  width: 100%;
  height: 100%;
  border: none;
  padding: 0;
}
.npd-add-stage {
  margin-top: 8px;
  background: transparent;
  border: 1px dashed rgba(251,191,36,0.40);
  color: #fbbf24;
  font-size: 11px; font-weight: 700;
  padding: 6px 12px;
  border-radius: 7px;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;
}
.npd-add-stage:hover {
  background: rgba(251,191,36,0.10);
  box-shadow: 0 0 12px rgba(251,191,36,0.20);
}

/* BRANCHES grid */
.npd-branches-grid { display: flex; flex-direction: column; gap: 6px; }
.npd-br-row {
  display: flex; align-items: center; gap: 12px;
  padding: 11px 14px;
  background: rgba(255,255,255,0.03);
  border: 1.5px solid rgba(255,255,255,0.08);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.18s;
}
.npd-br-row:hover { border-color: rgba(251,191,36,0.30); }
.npd-br-row.is-on {
  background: linear-gradient(135deg, rgba(251,191,36,0.10), rgba(217,119,6,0.04));
  border-color: rgba(251,191,36,0.55);
  box-shadow: 0 0 16px rgba(251,191,36,0.18);
}
.npd-br-cb {
  width: 22px; height: 22px;
  border-radius: 6px;
  background: rgba(255,255,255,0.04);
  border: 1.5px solid rgba(255,255,255,0.18);
  flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; color: transparent;
  font-weight: 900;
}
.npd-br-row.is-on .npd-br-cb {
  background: linear-gradient(135deg, #fbbf24, #d97706);
  border-color: #fbbf24;
  color: #000;
  box-shadow: 0 0 10px rgba(251,191,36,0.50);
}
.npd-br-icon {
  width: 32px; height: 32px;
  border-radius: 8px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  display: flex; align-items: center; justify-content: center;
  font-size: 9px; font-weight: 900; color: #fbbf24;
  flex-shrink: 0;
  letter-spacing: 0.3px;
}
.npd-br-row.is-on .npd-br-icon {
  background: rgba(251,191,36,0.15);
  border-color: rgba(251,191,36,0.40);
}
.npd-br-info { flex: 1; min-width: 0; }
.npd-br-name {
  font-size: 13px; font-weight: 800;
  color: #fff;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.npd-br-row.is-on .npd-br-name { color: #fbbf24; }
.npd-br-meta {
  font-size: 10px;
  color: rgba(255,255,255,0.45);
  margin-top: 1px;
}
.npd-br-tag {
  padding: 3px 8px;
  border-radius: 5px;
  font-size: 9px; font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  flex-shrink: 0;
}
.npd-br-tag.is-personal {
  background: rgba(99,102,241,0.18);
  color: #818cf8;
  border: 1px solid rgba(99,102,241,0.40);
}
.npd-br-tag.is-suc {
  background: rgba(255,255,255,0.04);
  color: rgba(255,255,255,0.55);
  border: 1px solid rgba(255,255,255,0.10);
}

.npd-summary {
  margin-top: 14px;
  padding: 11px 14px;
  background: rgba(34,197,94,0.06);
  border: 1px dashed rgba(34,197,94,0.40);
  border-radius: 9px;
  font-size: 11px;
  color: #4ade80;
  line-height: 1.5;
}

.npd-error {
  margin-top: 14px;
  padding: 10px 12px;
  background: rgba(239,68,68,0.10);
  border: 1px solid rgba(239,68,68,0.40);
  border-radius: 8px;
  font-size: 11px;
  color: #f87171;
  font-weight: 700;
}

.npd-foot {
  padding: 14px 22px;
  border-top: 1px solid rgba(255,255,255,0.06);
  display: flex; justify-content: flex-end; gap: 8px;
  background: rgba(0,0,0,0.4);
  flex-shrink: 0;
}
.npd-btn {
  padding: 10px 18px;
  border-radius: 8px;
  font-size: 12px; font-weight: 800;
  cursor: pointer;
  border: none;
  font-family: inherit;
}
.npd-btn:disabled { opacity: 0.5; cursor: wait; }
.npd-cancel {
  background: rgba(255,255,255,0.04);
  color: rgba(255,255,255,0.65);
  border: 1px solid rgba(255,255,255,0.08);
}
.npd-cancel:hover { background: rgba(255,255,255,0.08); }
.npd-save {
  background: linear-gradient(135deg, #fbbf24, #d97706);
  color: #000;
  box-shadow: 0 0 16px rgba(251,191,36,0.30);
}
.npd-save:hover:not(:disabled) {
  box-shadow: 0 0 24px rgba(251,191,36,0.55);
}
</style>
